export type DanceStatus = 'generating' | 'training' | 'success' | 'failed';
export type DanceType = 'simulation' | 'model';
export type OrderStatus = 'pending' | 'processing' | 'completed' | 'closed';
export type OrderType = 'simulation' | 'training';

export interface Dance {
  id: string;
  name: string;
  type: DanceType;
  status: DanceStatus;
  stage?: string;
  duration: string;
  robotModel: string;
  imageUrl: string;
  videoUrl?: string;
  progress?: number;
  createdAt: string;
  /** 所属仿真 ID（训练模型时使用） */
  simulationId?: string;
  /** 媒体 ID（查询商品报价时使用） */
  mediaId?: string;
}

export interface Order {
  id: string;
  orderNo: string;
  type: OrderType;
  typeLabel: string;
  amount: number;
  status: OrderStatus;
  danceName: string;
  robotModel: string;
  createdAt: string;
  paymentMethod?: string;
  closeReason?: string;
}

export type EditMode = 'freeAll' | 'proportionalAll' | 'currentFrame';

export interface KeyframeInterval {
  id: string;
  name: string;
  startFrame: number;
  endFrame: number;
  frames: JointAngles[];
  color: string;
}

export interface Keyframe {
  id: string;
  time: number;
  joints: JointAngles;
}

export interface KeyframeData {
  id: string;
  frame: number;
  joints: JointAngles;
}

export interface JointAngles {
  head_yaw_joint: number;
  head_pitch_joint: number;
  waist_yaw_joint: number;
  waist_roll_joint: number;
  waist_pitch_joint: number;
  left_shoulder_pitch_joint: number;
  left_shoulder_roll_joint: number;
  left_shoulder_yaw_joint: number;
  left_elbow_pitch_joint: number;
  left_elbow_yaw_joint: number;
  left_wrist_pitch_joint: number;
  left_wrist_roll_joint: number;
  left_wrist_yaw_joint: number;
  right_shoulder_pitch_joint: number;
  right_shoulder_roll_joint: number;
  right_shoulder_yaw_joint: number;
  right_elbow_pitch_joint: number;
  right_elbow_yaw_joint: number;
  right_wrist_pitch_joint: number;
  right_wrist_roll_joint: number;
  right_wrist_yaw_joint: number;
  left_hip_pitch_joint: number;
  left_hip_roll_joint: number;
  left_hip_yaw_joint: number;
  left_knee_pitch_joint: number;
  left_ankle_pitch_joint: number;
  left_ankle_roll_joint: number;
  right_hip_pitch_joint: number;
  right_hip_roll_joint: number;
  right_hip_yaw_joint: number;
  right_knee_pitch_joint: number;
  right_ankle_pitch_joint: number;
  right_ankle_roll_joint: number;
}

export type JointKey = keyof JointAngles;

export interface JointDef {
  key: JointKey;
  label: string;
  labelEn: string;
  min: number;
  max: number;
  default: number;
  icon?: string;
}

export const JOINT_DEFS: JointDef[] = [
  // 头部
  { key: 'head_yaw_joint', label: '头部偏航关节', labelEn: '(head_yaw_joint)', min: -45, max: 45, default: 0 },
  { key: 'head_pitch_joint', label: '头部俯仰关节', labelEn: '(head_pitch_joint)', min: -45, max: 45, default: 0 },
  
  // 腰部
  { key: 'waist_yaw_joint', label: '腰部偏航关节', labelEn: '(waist_yaw_joint)', min: -90, max: 90, default: 0 },
  { key: 'waist_roll_joint', label: '腰部滚转关节', labelEn: '(waist_roll_joint)', min: -45, max: 45, default: 0 },
  { key: 'waist_pitch_joint', label: '腰部俯仰关节', labelEn: '(waist_pitch_joint)', min: -45, max: 45, default: 0 },

  // 左臂
  { key: 'left_shoulder_pitch_joint', label: '左肩俯仰关节', labelEn: '(left_shoulder_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_shoulder_roll_joint', label: '左肩滚转关节', labelEn: '(left_shoulder_roll_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_shoulder_yaw_joint', label: '左肩偏航关节', labelEn: '(left_shoulder_yaw_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_elbow_pitch_joint', label: '左肘俯仰关节', labelEn: '(left_elbow_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_elbow_yaw_joint', label: '左肘偏航关节', labelEn: '(left_elbow_yaw_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_wrist_pitch_joint', label: '左腕俯仰关节', labelEn: '(left_wrist_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_wrist_roll_joint', label: '左腕滚转关节', labelEn: '(left_wrist_roll_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_wrist_yaw_joint', label: '左腕偏航关节', labelEn: '(left_wrist_yaw_joint)', min: -180, max: 180, default: 0 },

  // 右臂
  { key: 'right_shoulder_pitch_joint', label: '右肩俯仰关节', labelEn: '(right_shoulder_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_shoulder_roll_joint', label: '右肩滚转关节', labelEn: '(right_shoulder_roll_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_shoulder_yaw_joint', label: '右肩偏航关节', labelEn: '(right_shoulder_yaw_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_elbow_pitch_joint', label: '右肘俯仰关节', labelEn: '(right_elbow_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_elbow_yaw_joint', label: '右肘偏航关节', labelEn: '(right_elbow_yaw_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_wrist_pitch_joint', label: '右腕俯仰关节', labelEn: '(right_wrist_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_wrist_roll_joint', label: '右腕滚转关节', labelEn: '(right_wrist_roll_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_wrist_yaw_joint', label: '右腕偏航关节', labelEn: '(right_wrist_yaw_joint)', min: -180, max: 180, default: 0 },

  // 左腿
  { key: 'left_hip_pitch_joint', label: '左髋俯仰关节', labelEn: '(left_hip_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_hip_roll_joint', label: '左髋滚转关节', labelEn: '(left_hip_roll_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_hip_yaw_joint', label: '左髋偏航关节', labelEn: '(left_hip_yaw_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_knee_pitch_joint', label: '左膝俯仰关节', labelEn: '(left_knee_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_ankle_pitch_joint', label: '左踝俯仰关节', labelEn: '(left_ankle_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'left_ankle_roll_joint', label: '左踝滚转关节', labelEn: '(left_ankle_roll_joint)', min: -180, max: 180, default: 0 },

  // 右腿
  { key: 'right_hip_pitch_joint', label: '右髋俯仰关节', labelEn: '(right_hip_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_hip_roll_joint', label: '右髋滚转关节', labelEn: '(right_hip_roll_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_hip_yaw_joint', label: '右髋偏航关节', labelEn: '(right_hip_yaw_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_knee_pitch_joint', label: '右膝俯仰关节', labelEn: '(right_knee_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_ankle_pitch_joint', label: '右踝俯仰关节', labelEn: '(right_ankle_pitch_joint)', min: -180, max: 180, default: 0 },
  { key: 'right_ankle_roll_joint', label: '右踝滚转关节', labelEn: '(right_ankle_roll_joint)', min: -180, max: 180, default: 0 },
];

export function emptyJoints(): JointAngles {
  return JOINT_DEFS.reduce((acc, j) => { acc[j.key] = j.default; return acc; }, {} as JointAngles);
}

export const INTERVAL_COLORS = ['#4F6EF7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];
